"use client";

import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { AgentState } from "@huub-web/ui/types";
import {
  AIAgentIconButton,
  CircularProgress,
  Flex,
  Reveal,
  Tooltip,
  type ChatHistoryListProps,
  type ThemeName,
  type ChatViewProps,
} from "@huub-web/ui";

import { useNavigationState } from "../../providers";

import { menuItemsWrapperStyle } from "../Navigation/NavigationMenu.css";
import { SideContent, type SideContentProps } from "../SideContent/SideContent";
import { CompactAgent } from "./components/CompactAgent";
import { InnerContent } from "./components/InnerContent";
import { circularProgressStyle } from "./SideAgentContent.css";

import type { ChatConversation } from "@huub-web/ui/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SideAgentContentProps<ChatMessageData = any> = SideContentProps & {
  /**
   * The theme to use
   */
  theme?: ThemeName;
  /**
   * Whether to enable debug mode
   */
  debug?: boolean;
  /**
   * The trailing content to display in the bar
   */
  barTrailing?: ReactNode;
  /**
   * The footer content to display
   */
  footer?: ReactNode;
  /**
   * Whether the agent button should be disabled in the bar
   */
  disableAgent?: boolean;
  /**
   * The extra slides to display
   */
  slides?: Record<SideAgentSlide, ReactNode>;
  /**
   * The current slide ID to display
   */
  slide?: SideAgentSlide;
  /**
   * The conversation to display
   */
  conversation?: ChatConversation<ChatMessageData>;
  /**
   * The state of the agent
   */
  state?: AgentState;
  /**
   * Whether the agent is loading
   */
  agentIsLoading?: boolean;
  /**
   * The callback to handle the history chat click
   */
  onHistoryChatClick?: ChatHistoryListProps["onChatClick"];
  /**
   * The callback to handle the slide change
   */
  onSlideChange?: (slide: SideAgentSlide) => void;
  /**
   * The callback to handle the AI response trailing
   */
  mapAIResponseTrailing?: ChatViewProps["mapAIResponseTrailing"];
  /**
   * The callback to handle the back button click
   */
  onBackButtonClick?: () => void;
};

export type SideAgentSlide = "agent" | "history" | string;

export const SIDE_CONTENT_SCROLL_EMIT_TAG = "sideContentScroll";

/**
 * Basic side agent component that provides a collapsible side panel
 */
export const SideAgentContent: FC<SideAgentContentProps> = ({
  barTrailing,
  conversation,
  disableAgent,
  slide = "agent",
  agentIsLoading = false,
  theme,
  slides,
  debug = false,
  lastTrailing,
  footer,
  state,
  trailing,
  mapAIResponseTrailing,
  onHistoryChatClick,
  onBackButtonClick,
  onSlideChange,
}) => {
  const { t } = useTranslation();
  const { sideContentVariant, setSideContentOpen } = useNavigationState();

  const handleBackButtonClick = () => {
    onBackButtonClick?.();
  };

  return sideContentVariant === "compact" ? (
    <CompactAgent disableAgent={disableAgent}>
      <InnerContent
        debug={debug}
        variant="compact"
        size="s"
        conversation={conversation}
        state={state}
        theme={theme}
        trailing={trailing}
        footer={footer}
        slide={slide}
        slides={slides}
        onHistoryChatClick={onHistoryChatClick}
        onSlideChange={onSlideChange}
        onBackButtonClick={handleBackButtonClick}
        mapAIResponseTrailing={mapAIResponseTrailing}
      />
    </CompactAgent>
  ) : (
    <SideContent
      lastTrailing={lastTrailing}
      trailing={
        <InnerContent
          debug={debug}
          theme={theme}
          variant="sideContent"
          conversation={conversation}
          state={state}
          trailing={trailing}
          footer={footer}
          slide={slide}
          slides={slides}
          onHistoryChatClick={onHistoryChatClick}
          onSlideChange={onSlideChange}
          mapAIResponseTrailing={mapAIResponseTrailing}
          onBackButtonClick={handleBackButtonClick}
        />
      }
      bar={
        <div className={menuItemsWrapperStyle()}>
          <Reveal>
            {!!disableAgent === false && (
              <Tooltip value={t("sideContent.open")} placement="left">
                <AIAgentIconButton onClick={() => setSideContentOpen(true)}>
                  <Reveal className={circularProgressStyle}>
                    {agentIsLoading && (
                      <CircularProgress
                        progress={25}
                        variant="opaque"
                        absolute
                        infinite
                      />
                    )}
                  </Reveal>
                </AIAgentIconButton>
              </Tooltip>
            )}
          </Reveal>
          {barTrailing && <Flex grow />}
          {barTrailing}
        </div>
      }
    />
  );
};
